import type { TextStyle } from 'react-native';

/**
 * Типографическая шкала — семь ролей вместо двадцати двух произвольных кеглей.
 *
 * <p><b>Что было.</b> В стилях стояли размеры 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 22,
 * 24, 25, 26, 28, 30, 34, 36, 38, 48. Соседние 13 / 14 / 15 использовались 53, 28 и 33 раза —
 * три почти неразличимых размера конкурировали за одну и ту же роль, и выбор между ними
 * делался каждый раз заново.
 *
 * <p><b>И всё было мелким.</b> Имя собеседника в списке шло кеглем 14, превью — 13, время —
 * 12. Для сравнения: в системном списке сообщений iOS имя набрано 17 pt, в Telegram — 16 sp.
 * Экран, собранный из 11–15, читается как настольное приложение, ужатое в телефон.
 *
 * <p><b>И всё было полужирным.</b> Из 77 объявлений `fontWeight` 70 приходились на 600 и 700,
 * обычного 400 не было нигде. Когда полужирным набрано всё, акцентировать становится нечем:
 * вес перестаёт работать как средство иерархии.
 *
 * <p><b>Интерлиньяж.</b> `lineHeight` был задан 26 раз на 29 744 строки кода — то есть почти
 * нигде. Умолчание RN для кириллицы при 13–15 px тесновато, а в превью и описаниях каналов
 * текст идёт в две-три строки. Здесь интерлиньяж — часть роли, а не отдельное решение.
 *
 * <p><b>Почему семь, а не шесть.</b> Кроме пяти «текстовых» ролей нужны две служебные: `micro`
 * для оверлайнов («КАНАЛ», «ГРУППА») и подписей чипов, и `largeTitle` для заголовка экрана.
 * Шкала повторяет форму системной (iOS Text Styles): плотно внизу, где живёт основная масса
 * подписей, и с разрывами вверху, где размер сам по себе работает акцентом.
 */

type Role = Required<Pick<TextStyle, 'fontSize' | 'lineHeight' | 'fontWeight'>>;

/**
 * Готовые роли. Каждая — законченный стиль текста без цвета: цвет приходит из палитры темы,
 * потому что одна и та же роль на светлой и тёмной поверхности красится по-разному.
 */
export const TYPE = {
  /** Оверлайн и подпись чипа: «КАНАЛ», «ГРУППА», вкладки настроек. Всегда короткий. */
  micro: { fontSize: 11, lineHeight: 14, fontWeight: '600' },
  /** Служебная величина: время в пузыре, счётчик, таймер, подпись под полем. */
  caption: { fontSize: 12, lineHeight: 16, fontWeight: '500' },
  /** Второстепенный текст: превью чата, описание канала, подсказка под заголовком. */
  footnote: { fontSize: 13, lineHeight: 18, fontWeight: '400' },
  /** Компактный текст интерфейса: строка в модалке, подпись поля, пункт списка настроек. */
  body: { fontSize: 15, lineHeight: 21, fontWeight: '400' },
  /**
   * Ступень 17 — имя в строке списка, заголовок карточки, название раздела.
   *
   * <p>ТЕКСТ СООБЩЕНИЯ живёт на этой же ступени, но обычным весом: `weight('headline', '400')`.
   * Это не обход шкалы, а её замысел — ровно так устроены системные стили iOS, где `body` и
   * `headline` оба 17 pt и различаются только насыщенностью. Заводить отдельный размер 16
   * между `body` (15) и `headline` (17) значило бы вернуть ту самую тройку неразличимых
   * соседей, ради ухода от которой шкала и собиралась.
   */
  headline: { fontSize: 17, lineHeight: 22, fontWeight: '600' },
  /** Заголовок модалки или листа. */
  title: { fontSize: 22, lineHeight: 28, fontWeight: '700' },
  /** Заголовок экрана — «Чаты». Единственный на экране. */
  largeTitle: { fontSize: 30, lineHeight: 36, fontWeight: '700' },
} as const satisfies Record<string, Role>;

type TypeRole = keyof typeof TYPE;

/**
 * Та же роль другим весом.
 *
 * <p>Нужна там, где размер уже выбран правильно, а вес должен отличаться от умолчания роли:
 * имя отправителя в групповом пузыре — это `footnote`, но полужирный, потому что стоит над
 * текстом сообщения того же кегля. Отдельная роль под каждый такой случай вернула бы ту самую
 * россыпь, ради ухода от которой шкала и заводилась.
 *
 * <p>Менять здесь можно ТОЛЬКО вес: размер и интерлиньяж роли остаются, иначе ступень
 * перестаёт быть ступенью.
 */
export function weight(role: TypeRole, fontWeight: TextStyle['fontWeight']): Role {
  return { ...TYPE[role], fontWeight } as Role;
}

/**
 * Разрядка для набранного капслоком оверлайна.
 *
 * <p>Прописные без разрядки слипаются — это единственное место, где `letterSpacing` в
 * приложении оправдан, и держать его лучше рядом со шкалой, чем вспоминать число на месте.
 */
export const OVERLINE: TextStyle = { ...TYPE.micro, letterSpacing: 0.6 };
